import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Badge, Card, Button, Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import { getMyAssignmentsAction, updateAssignmentStatusAction } from '../../store/reducers/personalTask';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import { TASK_STATUS_CONST } from '../../store/constants';
import { toast } from 'react-toastify';
import { userManager } from '@app/utils/oidc';
import axios from 'axios';
import DatePicker from '@app/components/CustomDatePicker';
import moment from 'moment';
import { getToken } from '../../utils/helper';
import { PERSONALTASK_API, PROJECT_FILE_MANAGER_API, BaseGatewayImage, ANNUAL_JOB_API } from '../../utils/apiUrl';
import { Get, Post, DeleteSingle } from '../../utils/httpProvider';
import { member_c9, admin_c9, checkIsAdmin, checkIsMemberC9, EXTRAORDINARY_JOB_GROUP_NAME } from '../../utils/define';

const getStatusStyle = (color) => {
    const map = {
        'secondary': { bg: '#f6f8fa', text: '#57606a', border: '#d0d7de' },
        'info': { bg: '#ddf4ff', text: '#0969da', border: '#54aeff' },
        'warning': { bg: '#fff8c5', text: '#9a6700', border: '#d4a72c' },
        'success': { bg: '#dafbe1', text: '#1a7f37', border: '#2da44e' },
        'danger': { bg: '#ffebe9', text: '#cf222e', border: '#ff8182' }
    };
    return map[color] || map['secondary'];
};

const toDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) return '';
    const d = String(dt.getDate()).padStart(2, '0');
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const y = dt.getFullYear();
    return `${d}/${m}/${y}`;
};

const toDisplayDateTime = (dateStr) => {
    if (!dateStr) return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>N/A</span>;
    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>N/A</span>;
    const d = String(dt.getDate()).padStart(2, '0');
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const y = dt.getFullYear();
    const h = String(dt.getHours()).padStart(2, '0');
    const min = String(dt.getMinutes()).padStart(2, '0');
    const s = String(dt.getSeconds()).padStart(2, '0');
    return (
        <div className="d-flex flex-column align-items-end" style={{ lineHeight: '1.2' }}>
            <span style={{ fontWeight: 700, fontSize: '12px', color: '#202124' }}>{`${d}/${m}/${y}`}</span>
            <span style={{ fontSize: '11px', color: '#5f6368', fontWeight: 500 }}>{`${h}:${min}:${s}`}</span>
        </div>
    );
};

const getDeadlineWarning = (assignment) => {
    if (assignment.status === 3 || assignment.status === 4 || !assignment.endDate) return null;
    const now = new Date();
    const end = new Date(assignment.endDate);
    const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { type: 'overdue', label: 'Quá hạn', color: '#721c24', bg: '#f8d7da', border: '#f5c6cb' };
    if (diffDays <= 3) return { type: 'soon', label: `Còn ${diffDays} ngày`, color: '#856404', bg: '#fff3cd', border: '#ffeeba' };
    return null;
};

const COLUMN_STYLE_MAP = {
    0: { headerBg: '#e1e5f2', headerText: '#333333', border: '#c4ccdd', bodyBg: '#f7f9fc' },
    1: { headerBg: '#d3e5ff', headerText: '#245b8f', border: '#aac6ff', bodyBg: '#f3f7ff' },
    3: { headerBg: '#cef4e4', headerText: '#247a55', border: '#a0e0c6', bodyBg: '#f1fbf6' },
    4: { headerBg: '#f8d7da', headerText: '#721c24', border: '#f5c6cb', bodyBg: '#fff5f5' },
};

const PersonalKanban = ({ isNested = false }) => {
    const dispatch = useDispatch();
    const { myAssignments } = useSelector((state) => state.personalTask);
    const { listAccount } = useSelector((state) => state.account);

    const [filterFrom, setFilterFrom] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().split('T')[0];
    });
    const [filterTo, setFilterTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [draggingId, setDraggingId] = useState(null);
    const [detailItem, setDetailItem] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [catalogJobs, setCatalogJobs] = useState([]);
    const [selectedAnnualJobId, setSelectedAnnualJobId] = useState('');
    const [appliedAnnualJobId, setAppliedAnnualJobId] = useState('');

    // Evidence file state
    const [evidenceModal, setEvidenceModal] = useState(null);
    const [evidenceFiles, setEvidenceFiles] = useState([]);
    const [uploadingEvidence, setUploadingEvidence] = useState(false);
    const evidenceInputRef = React.useRef(null);

    useEffect(() => {
        dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        dispatch(getMyAssignmentsAction({}));
        userManager.getUser().then(user => setCurrentUser(user));
        Get(ANNUAL_JOB_API.GET_ALL_ACTIVE, null, null)
            .then(res => { if (res) setCatalogJobs(Array.isArray(res) ? res : (res.$values || [])); })
            .catch(err => console.error('Lỗi tải danh mục công việc:', err));
    }, [dispatch]);

    const handleFilter = () => {
        setAppliedAnnualJobId(selectedAnnualJobId);
        dispatch(getMyAssignmentsAction({
            from: filterFrom || undefined,
            to: filterTo || undefined,
            selectedUserId: selectedUserId || undefined,
        }));
    };

    const userRoles = useMemo(() => {
        return currentUser?.profile?.role
            ? Array.isArray(currentUser.profile.role)
                ? currentUser.profile.role
                : [currentUser.profile.role]
            : [];
    }, [currentUser]);

    const isAdmin = useMemo(() => checkIsAdmin(userRoles), [userRoles]);

    const getDisplayName = (userId) => {
        if (!userId) return 'Không rõ';
        const key = String(userId).trim().toLowerCase();

        const accInfo = listAccount?.find(a => {
            const id1 = String(a.accountId || a.AccountId || '').toLowerCase();
            const id2 = String(a.id || a.Id || a.account?.id || a.Account?.Id || '').toLowerCase();
            const usr1 = String(a.account?.username || a.Account?.Username || '').toLowerCase();
            const usr2 = String(a.username || a.Username || '').toLowerCase();
            return id1 === key || id2 === key || usr1 === key || usr2 === key;
        });

        if (!accInfo) return userId;

        const lastName = accInfo.lastName || accInfo.LastName || accInfo.accountAuthInfo?.lastName || '';
        const middleName = accInfo.middleName || accInfo.MiddleName || accInfo.accountAuthInfo?.middleName || '';
        const firstName = accInfo.firstName || accInfo.FirstName || accInfo.accountAuthInfo?.firstName || '';
        const fullName = `${lastName} ${middleName} ${firstName}`.replace(/\s+/g, ' ').trim();
        const username = accInfo.username || accInfo.Username || accInfo.account?.username || accInfo.account?.Username;

        if (fullName && username) return `${fullName} (${username})`;
        return fullName || username || userId;
    };

    const kpiAccounts = useMemo(() => {
        if (!listAccount || !Array.isArray(listAccount)) return [];
        return listAccount.filter(item => {
            const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
            const roles = acc?.roles || acc?.Roles || item.roles || item.Roles || [];
            return checkIsMemberC9(roles);
        });
    }, [listAccount]);

    const memberOptions = useMemo(() => {
        const list = kpiAccounts.length > 0 ? kpiAccounts : listAccount;
        if (!list || !Array.isArray(list)) return [];
        return list.map(acc => {
            const uId = acc.accountId || acc.id || acc.Id;
            const name = getDisplayName(uId);
            return {
                value: String(uId),
                label: name
            };
        });
    }, [kpiAccounts, listAccount, getDisplayName]);

    const catalogJobOptions = useMemo(() => {
        const target = (EXTRAORDINARY_JOB_GROUP_NAME || '').trim().toLowerCase();
        const filtered = (catalogJobs || []).filter(j => {
            const groupName = (j.jobGroupName || j.JobGroupName || '').trim().toLowerCase();
            return groupName === target;
        });

        const list = filtered.length > 0 ? filtered : (catalogJobs || []);
        return list.map(j => ({
            value: String(j.id || j.Id),
            label: j.name || j.Name
        }));
    }, [catalogJobs]);

    const handleMoveTask = async (projectId, newStatus, targetUserId) => {
        try {
            await dispatch(updateAssignmentStatusAction({ projectId, data: { status: newStatus, targetUserId } })).unwrap();
            dispatch(getMyAssignmentsAction({
                from: filterFrom || undefined,
                to: filterTo || undefined,
            }));

            // Cập nhật detailItem nếu đang mở để UI đồng bộ ngay
            if (detailItem && detailItem.projectId === projectId) {
                setDetailItem(prev => ({
                    ...prev,
                    status: newStatus,
                    members: prev.members.map(m => {
                        // Cập nhật record của chính mình trong danh sách member
                        // Ở đây m.userId có thể là string hoặc Guid object tùy API
                        const isMe = String(m.userId || m.UserId) === String(prev.userId || prev.UserId);
                        if (isMe) {
                            return { ...m, status: newStatus, Status: newStatus, actualEndDate: newStatus === 3 ? new Date().toISOString() : null };
                        }
                        return m;
                    })
                }));
            }

            toast.success('Cập nhật trạng thái thành công');
        } catch {
            toast.error('Cập nhật thất bại');
        }
    };

    const fetchEvidenceFiles = async (projectId) => {
        const res = await Get(`${PERSONALTASK_API.GET_EVIDENCE}/${projectId}`);
        if (res) {
            setEvidenceFiles(res);
        } else {
            setEvidenceFiles([]);
        }
    };

    const openEvidenceModal = (item) => {
        setEvidenceModal(item);
        fetchEvidenceFiles(item.projectId);
    };

    const handleUploadEvidence = async (event) => {
        if (!evidenceModal) return;
        const files = Array.from(event.target.files);
        if (!files || files.length === 0) return;

        // Validation
        if (evidenceFiles.length + files.length > 5) {
            toast.error('Mỗi thành viên chỉ được đính kèm tối đa 5 file minh chứng.');
            if (evidenceInputRef.current) evidenceInputRef.current.value = '';
            return;
        }

        const validFiles = files.filter(f => f.size / 1024 / 1024 <= 5);
        if (validFiles.length !== files.length) {
            toast.error('Có file vượt quá dung lượng 5MB và đã bị loại bỏ.');
        }

        if (validFiles.length === 0) {
            if (evidenceInputRef.current) evidenceInputRef.current.value = '';
            return;
        }

        setUploadingEvidence(true);
        const token = await getToken();

        try {
            for (let i = 0; i < validFiles.length; i++) {
                const file = validFiles[i];
                const config = {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `bearer ${token}`
                    }
                };
                const data = new FormData();
                data.append('file', file);

                const resUpload = await axios.post(PROJECT_FILE_MANAGER_API.UPLOAD, data, config);
                if (resUpload && resUpload.data && resUpload.data.length > 0) {
                    const uploadedData = resUpload.data[0];
                    const payload = {
                        FileName: uploadedData.fileName || file.name,
                        FilePath: uploadedData.filePath || uploadedData.thumbPath,
                        FileSize: file.size
                    };
                    await Post(`${PERSONALTASK_API.ADD_EVIDENCE}/${evidenceModal.projectId}`, null, payload);
                }
            }
            toast.success('Tải lên thành công!');
            fetchEvidenceFiles(evidenceModal.projectId);
        } catch (error) {
            const errResponse = error?.response?.data;
            if (errResponse) {
                toast.error(typeof errResponse === 'string' ? errResponse : 'Có lỗi xảy ra khi tải lên.');
            } else {
                toast.error('Có lỗi xảy ra khi tải lên.');
            }
        } finally {
            setUploadingEvidence(false);
            if (evidenceInputRef.current) evidenceInputRef.current.value = '';
        }
    };

    const handleDeleteEvidence = async (fileId) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa file này?')) {
            try {
                const res = await DeleteSingle(`${PERSONALTASK_API.DELETE_EVIDENCE}/${fileId}`);
                if (res.ok) {
                    toast.success('Xóa thành công!');
                    if (evidenceModal) fetchEvidenceFiles(evidenceModal.projectId);
                } else {
                    const errorText = await res.text();
                    toast.error(errorText || 'Xóa thất bại.');
                }
            } catch {
                toast.error('Xóa thất bại. Bạn có thể không có quyền xóa file này.');
            }
        }
    };

    const dowLoadFile = (url) => {
        try {
            if (url) {
                var a = document.createElement("a");
                a.href = url;
                let arrayUrrl = url.split("/");
                let fileName = arrayUrrl[arrayUrrl.length - 1];
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            }
        } catch (error) { }
    };

    const handleDragStart = (projectId, userId) => setDraggingId(`${projectId}_${userId}`);
    const handleDragEnd = () => setDraggingId(null);
    const handleDropOnColumn = async (newStatus) => {
        if (draggingId !== null) {
            const [pId, uId] = draggingId.split('_');
            const item = normalizedAssignments.find(a => String(a.projectId) === String(pId) && String(a.userId) === String(uId));
            if (item && item.status !== newStatus) {
                await handleMoveTask(item.projectId, newStatus, item.userId);
            }
            setDraggingId(null);
        }
    };

    const normalizedAssignments = useMemo(() => {
        return (myAssignments || []).map(a => {
            // API trả về Members (PascalCase) - thử tất cả biến thể có thể
            let rawMembers = a.Members ?? a.members ?? a.ProjectMembers ?? a.projectMembers ?? [];
            if (rawMembers && rawMembers.$values) {
                rawMembers = rawMembers.$values;
            }
            const rawArr = Array.isArray(rawMembers) ? rawMembers : [];

            // Normalize từng member để đảm bảo camelCase nhất quán
            const members = rawArr.map(m => ({
                ...m,
                userId: m.UserId ?? m.userId,
                status: m.Status ?? m.status ?? 0,
                memberWeight: m.MemberWeight ?? m.memberWeight ?? m.Weight ?? m.weight ?? null,
                actualEndDate: m.ActualEndDate ?? m.actualEndDate ?? null,
            }));

            return {
                ...a,
                projectId: a.ProjectId ?? a.projectId ?? a.Id ?? a.id,
                projectName: a.ProjectName ?? a.projectName ?? a.Name ?? a.name,
                projectDescription: a.ProjectDescription ?? a.projectDescription ?? a.Description ?? a.description,
                startDate: a.StartDate ?? a.startDate,
                endDate: a.EndDate ?? a.endDate,
                createdBy: a.CreatedBy ?? a.createdBy,
                userId: a.UserId ?? a.userId,
                annualJobId: a.AnnualJobId ?? a.annualJobId ?? a.annualJob?.id ?? a.AnnualJob?.Id ?? null,
                annualJobName: a.AnnualJobName ?? a.annualJobName ?? a.annualJob?.name ?? a.AnnualJob?.Name ?? null,
                jobGroupName: a.JobGroupName ?? a.jobGroupName ?? a.jobGroup?.name ?? a.JobGroup?.Name ?? null,
                // API trả về MyStatus (không phải Status) cho trạng thái cá nhân
                status: a.MyStatus ?? a.myStatus ?? a.Status ?? a.status ?? 0,
                projectStatus: a.ProjectStatus ?? a.projectStatus ?? a.Status ?? a.status ?? 0,
                progress: a.ProjectProgress ?? a.projectProgress ?? a.Progress ?? a.progress ?? 0,
                projectActualEndDate: a.ProjectActualEndDate ?? a.projectActualEndDate ?? a.ActualEndDate ?? a.actualEndDate,
                isReturned: a.IsReturned ?? a.isReturned ?? false,
                returnComment: a.ReturnComment ?? a.returnComment ?? null,
                members: members
            };
        });
    }, [myAssignments]);

    const filteredAssignments = useMemo(() => {
        if (!appliedAnnualJobId) return normalizedAssignments;
        return normalizedAssignments.filter(a => {
            const taskAnnualJobId = a.annualJobId ?? a.AnnualJobId ?? a.annualJob?.id ?? a.AnnualJob?.Id;
            return String(taskAnnualJobId || '') === String(appliedAnnualJobId);
        });
    }, [normalizedAssignments, appliedAnnualJobId]);

    const assignmentsByStatus = useMemo(() => {
        const result = {};
        TASK_STATUS_CONST.forEach(col => {
            result[col.value] = filteredAssignments.filter(a => Number(a.status) === Number(col.value));
        });
        return result;
    }, [filteredAssignments]);

    return (
        <div className={isNested ? "" : "container-fluid"}>
            {!isNested && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 border-bottom pb-3">
                    <div className="mb-3 mb-md-0">
                        <h2 className="mb-1 text-dark fw-bold">Nhiệm vụ cá nhân</h2>
                        <p className="text-muted mb-0" style={{ fontSize: '15px' }}>Các công việc được giao cho bạn</p>
                    </div>
                </div>
            )}

            <div className="card mb-4 border-0 shadow-sm bg-white p-3">
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-stretch align-items-lg-center gap-3">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <div className="d-flex align-items-center gap-2 mr-2">
                            <span className="fw-bold text-dark mr-1" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>📅 Thời gian:</span>
                            <div style={{ width: '130px' }}>
                                <DatePicker
                                    className="form-control"
                                    selected={filterFrom ? new Date(filterFrom) : null}
                                    onChange={(date) => setFilterFrom(date ? moment(date).format('YYYY-MM-DD') : '')}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                    title="Từ ngày"
                                    isClearable
                                />
                            </div>
                            <span className="text-muted fw-bold">–</span>
                            <div style={{ width: '130px' }}>
                                <DatePicker
                                    className="form-control"
                                    selected={filterTo ? new Date(filterTo) : null}
                                    onChange={(date) => setFilterTo(date ? moment(date).format('YYYY-MM-DD') : '')}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                    title="Đến ngày"
                                    isClearable
                                />
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 mr-2">
                            <span className="fw-bold text-dark" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>📋 Danh mục:</span>
                            <div style={{ width: '220px' }}>
                                <Select
                                    value={catalogJobOptions.find(opt => opt.value === String(selectedAnnualJobId)) || null}
                                    options={catalogJobOptions}
                                    onChange={(opt) => setSelectedAnnualJobId(opt ? opt.value : '')}
                                    placeholder="-- Tất cả danh mục --"
                                    isClearable
                                    isSearchable
                                    noOptionsMessage={() => 'Không tìm thấy danh mục'}
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '38px', height: '38px', borderColor: '#ced4da', fontSize: '14px' }),
                                        valueContainer: (base) => ({ ...base, height: '38px', padding: '0 8px' }),
                                        input: (base) => ({ ...base, margin: '0px' }),
                                        indicatorsContainer: (base) => ({ ...base, height: '38px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {isAdmin && (
                            <div className="d-flex align-items-center gap-2 mr-2">
                                <span className="fw-bold text-dark" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>👤 Nhân viên:</span>
                                <div style={{ width: '220px' }}>
                                    <Select
                                        value={memberOptions.find(opt => opt.value === String(selectedUserId)) || null}
                                        options={memberOptions}
                                        onChange={(selectedOption) => {
                                            setSelectedUserId(selectedOption ? selectedOption.value : '');
                                        }}
                                        placeholder="-- Tất cả --"
                                        isClearable
                                        isSearchable
                                        noOptionsMessage={() => 'Không tìm thấy nhân viên'}
                                        styles={{
                                            control: (base) => ({ ...base, minHeight: '38px', height: '38px', borderColor: '#ced4da', fontSize: '14px' }),
                                            valueContainer: (base) => ({ ...base, height: '38px', padding: '0 8px' }),
                                            input: (base) => ({ ...base, margin: '0px' }),
                                            indicatorsContainer: (base) => ({ ...base, height: '38px' }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                        }}
                                        menuPortalTarget={document.body}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="d-flex gap-2">
                            <Button variant="primary" onClick={handleFilter} style={{ padding: '6px 20px', fontSize: '15px' }}>Lọc</Button>
                            <Button variant="outline-secondary" onClick={() => {
                                setFilterFrom(''); setFilterTo(''); setSelectedUserId(''); setSelectedAnnualJobId(''); setAppliedAnnualJobId('');
                                dispatch(getMyAssignmentsAction({}));
                            }} style={{ padding: '6px 15px', fontSize: '15px' }}>Xoá lọc</Button>
                        </div>
                    </div>
                </div>
            </div>

            <Row>
                {TASK_STATUS_CONST.map(col => {
                    const list = assignmentsByStatus[col.value] || [];
                    const styleCfg = COLUMN_STYLE_MAP[col.value] || COLUMN_STYLE_MAP[0];

                    return (
                        <Col key={col.value} md={3} className="mb-4">
                            <div
                                className="card shadow-sm h-100 d-flex flex-column"
                                style={{ borderColor: styleCfg.border, borderWidth: 1, borderStyle: 'solid', backgroundColor: '#fff' }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => handleDropOnColumn(col.value)}
                            >
                                <div
                                    className="card-header d-flex align-items-center"
                                    style={{ backgroundColor: styleCfg.headerBg, borderBottomColor: styleCfg.border, borderBottomStyle: 'solid', borderBottomWidth: 1, color: styleCfg.headerText, fontWeight: 700, fontSize: 17, height: 60 }}
                                >
                                    <span>{col.label}</span>
                                    <div style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>{list.length}</div>
                                    <div style={{ width: 28 }}></div>
                                </div>

                                <div
                                    className="card-body p-2 flex-grow-1"
                                    style={{ minHeight: 400, backgroundColor: styleCfg.bodyBg }}
                                >
                                    {list.map(assignment => {
                                        const warning = getDeadlineWarning(assignment);
                                        const isBeingDragged = draggingId === `${assignment.projectId}_${assignment.userId}`;

                                        const userRoles = currentUser?.profile?.role
                                            ? Array.isArray(currentUser.profile.role)
                                                ? currentUser.profile.role
                                                : [currentUser.profile.role]
                                            : [];
                                        const isAdmin = userRoles.some(r => {
                                            const lowerR = String(r).toLowerCase();
                                            return lowerR === 'admin' || lowerR === 'admin_kpi_c9';
                                        });

                                        const isMyTask = String(currentUser?.profile?.sub) === String(assignment.userId);
                                        const currentUsername = currentUser?.profile?.name || currentUser?.profile?.preferred_username;
                                        const isAssigner = String(currentUsername) === String(assignment.createdBy);
                                        const canTransition = isMyTask || isAssigner || isAdmin;

                                        const currentMemberInfo = assignment.members?.find(m => String(m.userId || m.UserId) === String(assignment.userId || assignment.UserId));
                                        const evalPercent = currentMemberInfo?.evaluationPercent ?? currentMemberInfo?.EvaluationPercent;
                                        const evalScore = currentMemberInfo?.actualScore ?? currentMemberInfo?.ActualScore;
                                        const evalNote = currentMemberInfo?.evaluationNote ?? currentMemberInfo?.EvaluationNote;
                                        const isEvaluated = evalPercent !== null && evalPercent !== undefined;

                                        return (
                                            <Card
                                                key={`${assignment.projectId}_${assignment.userId}`}
                                                className="mb-2 shadow-sm"
                                                draggable={canTransition}
                                                onDragStart={() => handleDragStart(assignment.projectId, assignment.userId)}
                                                onDragEnd={handleDragEnd}
                                                style={{
                                                    opacity: isBeingDragged ? 0.5 : 1,
                                                    cursor: 'grab',
                                                    borderLeft: warning ? `4px solid ${warning.color}` : undefined,
                                                }}
                                            >
                                                <Card.Body className="p-3">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h5
                                                            className="mb-0"
                                                            style={{ fontWeight: 700, fontSize: 16, flex: 1, color: '#1a73e8', cursor: 'pointer', textDecoration: 'none' }}
                                                            onClick={() => setDetailItem(assignment)}
                                                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                        >
                                                            {assignment.projectName}
                                                        </h5>
                                                        {warning && (
                                                            <span style={{
                                                                marginLeft: 8,
                                                                padding: '3px 10px',
                                                                borderRadius: 999,
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                background: warning.bg,
                                                                color: warning.color,
                                                                border: `1px solid ${warning.border}`,
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {warning.label}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {(assignment.annualJobName || assignment.jobGroupName) && (
                                                        <div className="mb-2">
                                                            <span className="badge bg-light text-primary border" style={{ fontSize: '11px', fontWeight: 600, padding: '4px 8px' }}>
                                                                🏷️ {assignment.annualJobName || assignment.jobGroupName}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {assignment.projectDescription && (
                                                        <p className="text-muted mb-3" style={{ fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                                                            {assignment.projectDescription}
                                                        </p>
                                                    )}

                                                    <div style={{ fontSize: 13, color: '#555', lineHeight: '1.6' }} className="mb-3">
                                                        <div className="mb-2">
                                                            <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: 12 }}>
                                                                <span className="text-muted">Tiến độ nhóm:</span>
                                                                <span className="fw-bold text-success">{assignment.progress || 0}%</span>
                                                            </div>
                                                            <div className="progress" style={{ height: 6, borderRadius: 3 }}>
                                                                <div
                                                                    className="progress-bar bg-success"
                                                                    role="progressbar"
                                                                    style={{ width: `${assignment.progress || 0}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>

                                                        {assignment.createdBy && (
                                                            <div style={{ fontSize: '12px' }} className="mb-1 text-muted">
                                                                👤 Giao bởi: {getDisplayName(assignment.createdBy)}
                                                            </div>
                                                        )}

                                                        {assignment.userId && (
                                                            <div style={{ fontSize: '12px' }} className="mb-2 text-dark fw-bold">
                                                                👷 Người thực hiện: {getDisplayName(assignment.userId)}
                                                            </div>
                                                        )}

                                                        {assignment.startDate && (
                                                            <div style={{ fontSize: '12px' }} className="text-primary fw-bold mb-2">
                                                                📅 Bắt đầu: {toDisplayDate(assignment.startDate)}
                                                            </div>
                                                        )}

                                                        {assignment.endDate && (
                                                            <div style={{ fontSize: '12px' }} className="text-danger fw-bold mb-2">
                                                                ⏰ Hạn chót: {toDisplayDate(assignment.endDate)}
                                                            </div>
                                                        )}

                                                        {assignment.projectStatus === 1 && (
                                                            <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#e6f4ea', border: '1px solid #a8dab5' }}>
                                                                <div className="d-flex align-items-center mb-1" style={{ fontSize: '11px', fontWeight: 800, color: '#137333' }}>
                                                                    <span className="mr-1">🏁</span> DỰ ÁN HOÀN THÀNH
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center px-1">
                                                                    <span style={{ fontSize: '11px', color: '#3c4043', fontWeight: 600 }}>Thời gian:</span>
                                                                    <div style={{ color: '#137333', fontWeight: 700, fontSize: '12px' }}>
                                                                        {toDisplayDateTime(assignment.projectActualEndDate)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {isEvaluated && (
                                                            <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#e8f0fe', border: '1px solid #aecbfa' }}>
                                                                <div className="d-flex align-items-center justify-content-between mb-1" style={{ fontSize: '11px', fontWeight: 800, color: '#e61313ff' }}>
                                                                    <span>📝 ĐÁNH GIÁ KẾT QUẢ</span>
                                                                    <span className="badge badge-primary px-2 py-1" style={{ fontSize: '11px', backgroundColor: '#1a73e8', color: '#ffffff' }}>{evalPercent}%</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center px-1 mb-1">
                                                                    <span style={{ fontSize: '11px', color: '#3c4043', fontWeight: 600 }}>Điểm thực nhận:</span>
                                                                    <strong style={{ fontSize: '13px', color: '#137333' }}>{evalScore != null ? Number(evalScore).toFixed(2) : '-'} điểm</strong>
                                                                </div>
                                                                {evalNote && (
                                                                    <div className="mt-1 pt-1 px-1" style={{ fontSize: '12px', borderTop: '1px solid #d2e3fc', color: '#202124' }}>
                                                                        <span className="font-weight-bold" style={{ color: '#5f6368' }}>Lý do: </span>
                                                                        <span style={{ fontStyle: 'italic', fontWeight: 500 }}>{evalNote}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {(assignment.isReturned || currentMemberInfo?.isReturned || currentMemberInfo?.IsReturned) && (
                                                            <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeeba', color: '#856404' }}>
                                                                <div className="d-flex align-items-center mb-1" style={{ fontSize: '11px', fontWeight: 800, color: '#856404' }}>
                                                                    <span>⚠️ CÔNG VIỆC BỊ HOÀN TRẢ</span>
                                                                </div>
                                                                <div style={{ fontSize: '12px' }}>
                                                                    <strong>Lý do: </strong>
                                                                    <span style={{ fontStyle: 'italic' }}>
                                                                        {assignment.returnComment || currentMemberInfo?.returnComment || currentMemberInfo?.ReturnComment || 'Cần bổ sung/sửa đổi thông tin.'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="d-flex flex-column border-top pt-3 mt-2" style={{ gap: 8 }}>
                                                        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                                            {canTransition && TASK_STATUS_CONST.filter(c => c.value !== assignment.status && c.value !== 4).map(c => (
                                                                <Button
                                                                    key={c.value}
                                                                    variant={`outline-${c.color}`}
                                                                    style={{ fontSize: 12, padding: '4px 10px', fontWeight: 600 }}
                                                                    onClick={() => handleMoveTask(assignment.projectId, c.value, assignment.userId)}
                                                                >
                                                                    → {c.label}
                                                                </Button>
                                                            ))}
                                                        </div>
                                                        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                                            {assignment.status === 3 && (
                                                                <Button
                                                                    variant="outline-info"
                                                                    style={{ fontSize: 12, padding: '4px 12px', fontWeight: 600 }}
                                                                    onClick={() => openEvidenceModal(assignment)}
                                                                >
                                                                    📎 Minh chứng
                                                                </Button>
                                                            )}
                                                            <Button
                                                                variant="outline-secondary"
                                                                style={{ fontSize: 12, padding: '4px 12px', fontWeight: 600 }}
                                                                onClick={() => setDetailItem(assignment)}
                                                            >
                                                                Chi tiết
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            {/* Modal chi tiết công việc */}
            <Modal
                show={!!detailItem}
                onHide={() => setDetailItem(null)}
                size="xl"
                centered
                backdropClassName="custom-modal-backdrop"
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>{detailItem?.projectName}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: '#fff' }}>
                    {detailItem && (
                        <>
                            <p className="mb-4" style={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: '1.6', color: '#333' }}>
                                {detailItem.projectDescription || 'Không có mô tả'}
                            </p>

                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <div className="p-3 rounded bg-light h-100" style={{ fontSize: 14 }}>
                                        <div className="mb-2"><strong>📅 Ngày bắt đầu:</strong> {toDisplayDate(detailItem.startDate) || 'Chưa có'}</div>
                                        <div className="mb-2"><strong>⏰ Hạn chót:</strong> {toDisplayDate(detailItem.endDate) || 'Chưa có'}</div>
                                        <div className="mb-2"><strong>👤 Người giao:</strong> {getDisplayName(detailItem.createdBy)}</div>
                                        {(detailItem.annualJobName || detailItem.jobGroupName) && (
                                            <div className="mb-2"><strong>🏷️ Danh mục công việc:</strong> <span className="badge bg-info text-white">{detailItem.annualJobName || detailItem.jobGroupName}</span></div>
                                        )}
                                        <div className="mb-2">
                                            <strong>🎯 Dự án:</strong> {detailItem.projectStatus === 1
                                                ? <span className="text-success fw-bold">Hoàn thành lúc {toDisplayDateTime(detailItem.projectActualEndDate)}</span>
                                                : <span className="text-danger fw-bold">Chưa hoàn thành</span>
                                            }
                                        </div>
                                        <div className="mt-3 pt-2 border-top">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <strong>📊 Tiến độ chung của nhóm:</strong>
                                                <span className="fw-bold text-success">{detailItem.progress || 0}%</span>
                                            </div>
                                            <div className="progress" style={{ height: 10, borderRadius: 5 }}>
                                                <div
                                                    className="progress-bar bg-success progress-bar-striped progress-bar-animated"
                                                    role="progressbar"
                                                    style={{ width: `${detailItem.progress || 0}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 rounded border h-100" style={{ fontSize: 14 }}>
                                        <div className="mb-2 text-muted">Trạng thái của bạn:</div>
                                        {(() => {
                                            const currentStatus = TASK_STATUS_CONST.find(s => Number(s.value) === Number(detailItem.status));
                                            const style = getStatusStyle(currentStatus?.color || 'secondary');
                                            return (
                                                <span
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontWeight: '600',
                                                        backgroundColor: style.bg,
                                                        color: style.text,
                                                        border: `1px solid ${style.border}`,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        textTransform: 'uppercase',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: style.text, flexShrink: 0 }}></span>
                                                    {currentStatus?.label || 'Cần làm'}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {detailItem.members && detailItem.members.length > 0 && (
                                <>
                                    <h6 className="fw-bold mb-3 border-bottom pb-2">👥 Những người cùng tham gia ({detailItem.members.length})</h6>
                                    <div className="table-responsive border rounded" style={{ maxHeight: 250, overflowY: 'auto' }}>
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                                            <thead className="bg-light sticky-top">
                                                <tr>
                                                    <th className="ps-3 py-2">Thành viên</th>
                                                    <th className="text-center py-2" style={{ width: 80 }}>Điểm gốc</th>
                                                    <th className="text-center py-2">Trạng thái</th>
                                                    <th className="text-center py-2">Đánh giá</th>
                                                    <th className="text-center py-2">Ghi chú</th>
                                                    <th className="text-center py-2">Hoàn thành lúc</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detailItem.members.map((m, i) => {
                                                    const mStatusValue = m.status ?? m.Status ?? 0;
                                                    const mStatus = TASK_STATUS_CONST.find(s => Number(s.value) === Number(mStatusValue));
                                                    const mEvalPercent = m.evaluationPercent ?? m.EvaluationPercent;
                                                    const mActualScore = m.actualScore ?? m.ActualScore;
                                                    const mEvalNote = m.evaluationNote ?? m.EvaluationNote;
                                                    return (
                                                        <tr key={i}>
                                                            <td className="ps-3 py-2 align-middle fw-500">{getDisplayName(m.userId)}</td>
                                                            <td className="text-center py-2 align-middle fw-bold">
                                                                {m.memberWeight !== null && m.memberWeight !== undefined ? Number(m.memberWeight) : '-'}
                                                            </td>
                                                            <td className="text-center py-2 align-middle">
                                                                {(() => {
                                                                    const style = getStatusStyle(mStatus?.color || 'secondary');
                                                                    return (
                                                                        <span
                                                                            style={{
                                                                                fontSize: '0.65rem',
                                                                                padding: '2px 8px',
                                                                                borderRadius: '10px',
                                                                                fontWeight: '600',
                                                                                backgroundColor: style.bg,
                                                                                color: style.text,
                                                                                border: `1px solid ${style.border}`,
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                gap: '4px',
                                                                                textTransform: 'uppercase',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            <span style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: style.text, flexShrink: 0 }}></span>
                                                                            {mStatus?.label || 'Cần làm'}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="text-center py-2 align-middle">
                                                                {mEvalPercent != null ? (
                                                                    <div>
                                                                        <span className="badge bg-info text-white me-1" style={{ fontSize: '10px' }}>{mEvalPercent}%</span>
                                                                        <strong className="text-success" style={{ fontSize: '12px' }}>{mActualScore != null ? Number(mActualScore).toFixed(2) : '-'}</strong>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td className="text-center py-2 align-middle text-muted" style={{ fontSize: '12px', maxWidth: 150, whiteSpace: 'pre-wrap' }}>
                                                                {mEvalNote || '-'}
                                                            </td>
                                                            <td className="text-center py-2 align-middle text-muted">
                                                                {m.actualEndDate ? toDisplayDateTime(m.actualEndDate) : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" size="sm" onClick={() => setDetailItem(null)}>Đóng</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Minh chứng */}
            <Modal
                show={!!evidenceModal}
                onHide={() => setEvidenceModal(null)}
                size="lg"
                centered
                style={{ zIndex: 1060 }}
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
                        📎 Minh chứng hoàn thành
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <strong>Nhiệm vụ:</strong> {evidenceModal?.projectName}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0">Danh sách file đã tải lên ({evidenceFiles.length}/5)</h6>
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={uploadingEvidence || evidenceFiles.length >= 5}
                            onClick={() => evidenceInputRef.current.click()}
                        >
                            <i className="fas fa-upload me-1" /> Tải lên
                        </Button>
                        <input
                            type="file"
                            multiple
                            hidden
                            ref={evidenceInputRef}
                            onChange={handleUploadEvidence}
                        />
                    </div>

                    {uploadingEvidence && (
                        <div className="text-center py-3">
                            <div className="spinner-border text-primary" role="status"></div>
                            <div className="mt-2 text-muted">Đang tải lên...</div>
                        </div>
                    )}

                    {!uploadingEvidence && evidenceFiles.length === 0 && (
                        <div className="text-center text-muted py-4 bg-light rounded border">
                            Chưa có file minh chứng nào.
                        </div>
                    )}

                    {evidenceFiles.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-hover table-bordered mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Tên file</th>
                                        <th className="text-center" style={{ width: '100px' }}>Kích thước</th>
                                        <th className="text-center" style={{ width: '150px' }}>Ngày tải</th>
                                        <th className="text-center" style={{ width: '100px' }}>Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {evidenceFiles.map((file) => (
                                        <tr key={file.id}>
                                            <td className="align-middle">
                                                <a href={BaseGatewayImage + file.filePath} target="_blank" rel="noopener noreferrer">
                                                    {file.fileName}
                                                </a>
                                            </td>
                                            <td className="text-center align-middle">
                                                {file.fileSize ? (file.fileSize / 1024 / 1024).toFixed(2) + ' MB' : '-'}
                                            </td>
                                            <td className="text-center align-middle">
                                                {toDisplayDateTime(file.uploadedAt)}
                                            </td>
                                            <td className="text-center align-middle">
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    title="Xóa"
                                                    onClick={() => handleDeleteEvidence(file.id)}
                                                    className="me-1"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    title="Tải xuống"
                                                    onClick={() => dowLoadFile(BaseGatewayImage + file.filePath)}
                                                >
                                                    <i className="fas fa-download"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEvidenceModal(null)}>Đóng</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PersonalKanban;
